import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Orchestra vector globe.
 * Phases on load: fragmented scatter -> convergence into clusters ->
 * compression -> globe formation -> rotating thinking globe.
 * Hover fires neural pulses across connections; click reveals a controlled
 * output panel. Canvas 2D for performance (no three.js).
 */
export const OrchestraGlobe = () => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const burstRef = useRef(false);
  const [showOutput, setShowOutput] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isMobile = window.matchMedia("(max-width: 640px)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const N = isMobile ? 170 : 360;
    const K = 5;
    const GOLD = [203, 174, 120];
    const SILVER = [165, 172, 184];

    let W = 0;
    let H = 0;
    let cx = 0;
    let cy = 0;
    let radius = 0;

    const rand = (a, b) => a + Math.random() * (b - a);

    const clusters = [];
    for (let k = 0; k < K; k++) {
      const a = (k / K) * Math.PI * 2;
      clusters.push({ x: Math.cos(a) * 1.5, y: rand(-0.8, 0.8), z: Math.sin(a) * 1.5 });
    }

    const pts = [];
    const offset = 2 / N;
    const inc = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < N; i++) {
      const uy = i * offset - 1 + offset / 2;
      const r = Math.sqrt(Math.max(0, 1 - uy * uy));
      const phi = i * inc;
      const cl = clusters[i % K];
      pts.push({
        sx: rand(-2.4, 2.4),
        sy: rand(-1.8, 1.8),
        sz: rand(-1.6, 1.6),
        clx: cl.x + rand(-0.25, 0.25),
        cly: cl.y + rand(-0.25, 0.25),
        clz: cl.z + rand(-0.25, 0.25),
        ux: Math.cos(phi) * r,
        uy,
        uz: Math.sin(phi) * r,
        col: Math.random() < 0.32 ? GOLD : SILVER,
      });
    }

    let edges = [];
    const seen = new Set();
    for (let i = 0; i < N; i++) {
      const di = [];
      for (let j = 0; j < N; j++) {
        if (i === j) continue;
        const dx = pts[i].ux - pts[j].ux;
        const dy = pts[i].uy - pts[j].uy;
        const dz = pts[i].uz - pts[j].uz;
        di.push([dx * dx + dy * dy + dz * dz, j]);
      }
      di.sort((a, b) => a[0] - b[0]);
      for (let nn = 0; nn < 2; nn++) {
        const j = di[nn][1];
        const key = i < j ? i + "_" + j : j + "_" + i;
        if (!seen.has(key)) {
          seen.add(key);
          edges.push([i, j]);
        }
      }
    }

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx = W / 2;
      cy = H / 2;
      radius = Math.min(W, H) * 0.36;
    };
    resize();

    let angleY = 0;
    const angleX = -0.22;
    let tiltX = 0;
    let tiltY = 0;
    let targetTiltX = 0;
    let targetTiltY = 0;
    let mx = -9999;
    let my = -9999;
    let hovering = false;
    let start = performance.now();
    let lastT = start;
    let running = true;
    let ambientTimer = 0;
    const pulses = [];
    const projected = new Array(N);

    const ease = (x) => 1 - Math.pow(1 - x, 3);

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mx = e.clientX - rect.left;
      my = e.clientY - rect.top;
      targetTiltY = (mx / W - 0.5) * 0.5;
      targetTiltX = (my / H - 0.5) * 0.4;
      const dx = mx - cx;
      const dy = my - cy;
      hovering = Math.sqrt(dx * dx + dy * dy) < radius * 1.15;
    };
    const onLeave = () => {
      hovering = false;
      mx = -9999;
      my = -9999;
      targetTiltX = 0;
      targetTiltY = 0;
    };
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);

    const project = (p) => {
      const ay = angleY + tiltY;
      const ax = angleX + tiltX;
      const cosY = Math.cos(ay);
      const sinY = Math.sin(ay);
      const cosX = Math.cos(ax);
      const sinX = Math.sin(ax);
      const x1 = p.x * cosY - p.z * sinY;
      const z1 = p.x * sinY + p.z * cosY;
      const y1 = p.y * cosX - z1 * sinX;
      const z2 = p.y * sinX + z1 * cosX;
      const persp = 2.6 / (2.6 - z2 * 0.6);
      return { x: cx + x1 * radius * persp, y: cy + y1 * radius * persp, z: z2, s: persp };
    };

    const worldPos = (p, g) => {
      if (g >= 1) return { x: p.ux, y: p.uy, z: p.uz };
      if (g < 0.5) {
        const e = ease(g * 2);
        return {
          x: p.sx + (p.clx - p.sx) * e,
          y: p.sy + (p.cly - p.sy) * e,
          z: p.sz + (p.clz - p.sz) * e,
        };
      }
      const e = ease((g - 0.5) * 2);
      return {
        x: p.clx + (p.ux - p.clx) * e,
        y: p.cly + (p.uy - p.cly) * e,
        z: p.clz + (p.uz - p.clz) * e,
      };
    };

    const spawnPulse = (count) => {
      for (let nn = 0; nn < count; nn++) {
        if (pulses.length > 70) break;
        const e = edges[Math.floor(Math.random() * edges.length)];
        if (!e) continue;
        pulses.push({
          i: e[0],
          j: e[1],
          t: 0,
          dur: rand(0.6, 1.2),
          col: Math.random() < 0.5 ? GOLD : SILVER,
        });
      }
    };

    const frame = (now) => {
      const dt = Math.min((now - lastT) / 1000, 0.05);
      lastT = now;
      const T = (now - start) / 1000;
      const g = reduced ? 1 : Math.min(1, T / 4.2);

      if (!reduced) angleY += dt * 0.12;
      tiltX += (targetTiltX - tiltX) * 0.05;
      tiltY += (targetTiltY - tiltY) * 0.05;

      ctx.clearRect(0, 0, W, H);

      for (let i = 0; i < N; i++) projected[i] = project(worldPos(pts[i], g));

      const edgeBase = g < 0.5 ? 0 : (g - 0.5) / 0.5;
      if (edgeBase > 0.02) {
        for (let k = 0; k < edges.length; k++) {
          const a = projected[edges[k][0]];
          const b = projected[edges[k][1]];
          const depth = (a.z + b.z) / 2;
          const al = (0.05 + ((depth + 1) / 2) * 0.1) * edgeBase;
          ctx.beginPath();
          ctx.strokeStyle = "rgba(150,158,170," + al.toFixed(3) + ")";
          ctx.lineWidth = 1;
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      for (let i = 0; i < N; i++) {
        const pr = projected[i];
        const depth = (pr.z + 1) / 2;
        let size = (0.6 + depth * 1.8) * pr.s;
        let a = 0.18 + depth * 0.5;
        let near = false;
        if (hovering) {
          const dx = pr.x - mx;
          const dy = pr.y - my;
          if (dx * dx + dy * dy < 58 * 58) {
            near = true;
            a = Math.min(1, a + 0.4);
            size += 0.9;
          }
        }
        const c = pts[i].col;
        if (near) {
          ctx.save();
          ctx.shadowBlur = 10;
          ctx.shadowColor = "rgba(203,174,120,0.85)";
        }
        ctx.beginPath();
        ctx.fillStyle = "rgba(" + c[0] + "," + c[1] + "," + c[2] + "," + a.toFixed(3) + ")";
        ctx.arc(pr.x, pr.y, Math.max(0.4, size), 0, Math.PI * 2);
        ctx.fill();
        if (near) ctx.restore();
      }

      if (g >= 0.6 && !reduced) {
        ambientTimer -= dt;
        const rate = hovering ? 0.05 : 0.24;
        if (ambientTimer <= 0) {
          spawnPulse(hovering ? 3 : 1);
          ambientTimer = rate;
        }
        if (burstRef.current) {
          spawnPulse(16);
          burstRef.current = false;
        }
        for (let k = pulses.length - 1; k >= 0; k--) {
          const pu = pulses[k];
          pu.t += dt;
          const f = pu.t / pu.dur;
          if (f >= 1) {
            pulses.splice(k, 1);
            continue;
          }
          const a = projected[pu.i];
          const b = projected[pu.j];
          const x = a.x + (b.x - a.x) * f;
          const y = a.y + (b.y - a.y) * f;
          ctx.save();
          ctx.shadowBlur = 8;
          ctx.shadowColor = "rgba(" + pu.col[0] + "," + pu.col[1] + "," + pu.col[2] + ",0.9)";
          ctx.beginPath();
          ctx.fillStyle = "rgba(" + pu.col[0] + "," + pu.col[1] + "," + pu.col[2] + "," + (1 - f).toFixed(3) + ")";
          ctx.arc(x, y, 1.7, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }

      if (running && !reduced) rafRef.current = requestAnimationFrame(frame);
    };

    if (reduced) frame(performance.now());
    else rafRef.current = requestAnimationFrame(frame);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            if (!running && !reduced) {
              running = true;
              lastT = performance.now();
              rafRef.current = requestAnimationFrame(frame);
            }
          } else {
            running = false;
            cancelAnimationFrame(rafRef.current);
          }
        });
      },
      { threshold: 0.05 }
    );
    io.observe(wrap);

    const onResize = () => resize();
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      io.disconnect();
      window.removeEventListener("resize", onResize);
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const handleActivate = () => {
    burstRef.current = true;
    setShowOutput((v) => !v);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="orchestra-globe"
      className="relative overflow-hidden rounded-[1.4rem] border border-white/10 bg-white/[0.025] backdrop-blur-xl shadow-[0_24px_80px_rgba(0,0,0,0.5)]"
    >
      <div className="flex items-center justify-between border-b border-white/[0.06] px-6 py-4">
        <div className="flex items-center gap-2.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[#C4A061]/80 shadow-[0_0_10px_rgba(196,160,97,0.5)]" />
          <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/70">
            Orchestra // Vector Field
          </span>
        </div>
        <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/35">
          Interactive
        </span>
      </div>

      <div
        ref={wrapRef}
        onClick={handleActivate}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleActivate();
        }}
        data-testid="orchestra-globe-stage"
        className="relative h-[400px] w-full cursor-pointer select-none sm:h-[520px]"
      >
        <div className="pointer-events-none absolute inset-0 triarch-grid opacity-[0.1]" />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(520px 520px at 50% 50%, rgba(203,174,120,0.06), transparent 60%)",
          }}
        />
        <canvas
          ref={canvasRef}
          data-testid="orchestra-globe-canvas"
          aria-hidden="true"
          className="absolute inset-0 h-full w-full"
        />
        <span className="pointer-events-none absolute bottom-5 left-6 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-white/30">
          Hover to fire · Click for output
        </span>

        <AnimatePresence>
          {showOutput && (
            <motion.div
              data-testid="orchestra-output"
              initial={{ opacity: 0, y: 12, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: 8, filter: "blur(6px)" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="absolute bottom-5 left-1/2 w-[min(440px,86%)] -translate-x-1/2 rounded-xl border border-[#C9AE78]/35 bg-[#0B0D10]/85 p-4 backdrop-blur-xl shadow-[0_0_34px_rgba(203,174,120,0.2)]"
            >
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.28em] text-[#C9AE78]/85">
                Orchestra Output // Controlled
              </span>
              <div className="mt-2 h-px w-full bg-gradient-to-r from-[#C9AE78]/30 to-transparent" />
              <p className="mt-3 font-mono-accent text-xs leading-relaxed text-white/80">
                Currently ingesting data in a controlled development environment.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
